/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { useEffect } from 'react';
import { getStore } from '../../reducers';
import { grantToken } from '../../reducers/auth';

// Refresh this many ms before the access token expires. A wide window (5 min)
// means that even if the browser throttles this interval while the tab is in
// the background, we still have many chances to catch it before expiry —
// unlike a 60s window that a throttled timer easily skips past.
const REFRESH_LEAD_MS = 5 * 60 * 1000;

/**
 * Keep the session alive without user interaction:
 * 1. If a refresh token exists and the access token is near expiry, do a
 *    refresh_token grant.
 * 2. If there is NO refresh token (or it's been cleared), fall back to a silent
 *    re-auth (prompt=none hidden iframe) which rides the still-alive corporate
 *    SSO session — so an expired/absent refresh token does NOT force a login.
 * Also runs an immediate check when the tab becomes visible again, since a
 * backgrounded tab's interval may have been throttled and missed the window.
 */
export const useAutoRefreshToken = () => {
  useEffect(() => {
    const maybeRefresh = async () => {
      const store = getStore();
      const { auth } = store.getState();
      if (!auth.loggedIn || auth.grantTokenLoading) return;

      const nearExpiry =
        !!auth.expiresAt &&
        auth.expiresAt * 1000 - REFRESH_LEAD_MS <= Date.now();
      if (!nearExpiry) return;

      if (auth.refreshToken) {
        store.dispatch(
          grantToken({
            grant_type: 'refresh_token',
            refresh_token: auth.refreshToken,
          }),
        );
      } else {
        // No refresh token — try a silent re-auth via the SSO session before
        // the token fully lapses, so the user is never bounced to /login while
        // their SSO session is alive.
        try {
          const { silentReauth } = await import('../silentReauth');
          await silentReauth();
        } catch {
          /* interactive login will be required if this fails */
        }
      }
    };

    const interval = setInterval(maybeRefresh, 10000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
};
